/**
 * Outil « Série statistique » — calculs (pur).
 *
 * Valeurs entières de 0 à 20 (des notes, par exemple). Moyenne et médiane
 * exactes (fractions), étendue, quartiles selon la définition du lycée
 * (Q1 = plus petite valeur de la série triée telle qu'au moins 25 % des
 * valeurs lui soient inférieures ou égales), écart type de la population.
 */

import { rational, type ExactNumber } from '../../ui/exact';

export const MIN_VALUE = 0;
export const MAX_VALUE = 20;
export const MAX_COUNT = 25;

export const PRESETS: ReadonlyArray<{ id: string; label: string; values: readonly number[] }> = [
  { id: 'notes', label: 'Notes', values: [8, 10, 11, 12, 12, 14, 15, 17, 19] },
  { id: 'extreme', label: 'Valeur extrême', values: [2, 11, 12, 12, 13, 13, 14, 14, 15] },
  { id: 'tight', label: 'Groupée', values: [9, 10, 10, 10, 11] },
  { id: 'spread', label: 'Étalée', values: [2, 6, 10, 14, 18] },
];

export function sorted(values: readonly number[]): number[] {
  return [...values].sort((a, b) => a - b);
}

export function mean(values: readonly number[]): ExactNumber {
  const sum = values.reduce((s, v) => s + v, 0);
  return rational(sum, values.length);
}

/** Médiane : valeur du milieu, ou moyenne des deux du milieu (n pair). */
export function median(values: readonly number[]): ExactNumber {
  const s = sorted(values);
  const n = s.length;
  if (n % 2 === 1) return rational(s[(n - 1) / 2]!, 1);
  return rational(s[n / 2 - 1]! + s[n / 2]!, 2);
}

/** Rangs (1 à n) des valeurs qui font la médiane dans la série triée. */
export function medianRanks(n: number): number[] {
  return n % 2 === 1 ? [(n + 1) / 2] : [n / 2, n / 2 + 1];
}

export function range(values: readonly number[]): number {
  return Math.max(...values) - Math.min(...values);
}

/** Q1 et Q3 (définition du lycée) et leurs rangs dans la série triée. */
export function quartiles(values: readonly number[]): {
  q1: number;
  q3: number;
  r1: number;
  r3: number;
} {
  const s = sorted(values);
  const n = s.length;
  const r1 = Math.ceil(n / 4);
  const r3 = Math.ceil((3 * n) / 4);
  return { q1: s[r1 - 1]!, q3: s[r3 - 1]!, r1, r3 };
}

/** Variance de la population : moyenne des carrés des écarts à la moyenne. */
export function variance(values: readonly number[]): number {
  const m = values.reduce((s, v) => s + v, 0) / values.length;
  return values.reduce((s, v) => s + (v - m) ** 2, 0) / values.length;
}

export function standardDeviation(values: readonly number[]): number {
  return Math.sqrt(variance(values));
}

/** Somme écrite pour le calcul de la moyenne : « 8 + 10 + … + 19 » (abrégée au-delà de 8 termes). */
export function sumText(values: readonly number[]): string {
  const s = sorted(values);
  if (s.length <= 8) return s.join(' + ');
  return `${s.slice(0, 3).join(' + ')} + … + ${s[s.length - 1]}`;
}

/** Position verticale de chaque valeur dans le diagramme en points (empilées). */
export function stackLevels(values: readonly number[]): number[] {
  const seen = new Map<number, number>();
  return values.map((v) => {
    const k = seen.get(v) ?? 0;
    seen.set(v, k + 1);
    return k;
  });
}

/**
 * Valeurs extrêmes (règle de Tukey) : à plus de 1,5 écart interquartile
 * au-delà de Q1 ou de Q3. Sert seulement à choisir le message affiché.
 */
export function outliers(values: readonly number[]): number[] {
  if (values.length < 4) return [];
  const { q1, q3 } = quartiles(values);
  const iqr = q3 - q1;
  return values.filter((v) => v < q1 - 1.5 * iqr || v > q3 + 1.5 * iqr);
}
